"use client";

import React, { useEffect } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Send } from "lucide-react";
import { cn } from "@/lib/utils";
const FloatingField = ({
  control,
  name,
  label,
  type = "text",
  options,
  required,
  rules,
  helpText,
  placeholder
}: any) => {
  return <FormField control={control} name={name} rules={rules} render={({
    field
  }) => <FormItem className="relative pt-6 pb-4 space-y-0 group">
                    <FormControl>
                        {type === 'textarea' ? <textarea {...field} placeholder={placeholder} className="peer w-full resize-none rounded-none border-0 border-b-2 border-border bg-transparent px-0 py-2 text-foreground font-mono shadow-none focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_15px_-3px_var(--color-primary)] transition-all outline-none min-h-[120px] placeholder:text-transparent focus-visible:placeholder:text-muted-foreground/50" /> : type === 'select' ? <select {...field} className="peer w-full appearance-none rounded-none border-0 border-b-2 border-border bg-transparent px-0 py-2 text-foreground font-mono shadow-none focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_15px_-3px_var(--color-primary)] transition-all outline-none">
                                <option value="" disabled hidden></option>
                                {options?.map((opt: string, i: number) => <option key={i} value={opt} data-index={i} className="bg-secondary text-foreground">
                                        {opt}
                                    </option>)}
                            </select> : <Input {...field} type={type} placeholder={placeholder} className="peer w-full rounded-none border-0 border-b-2 border-border bg-transparent px-0 py-2 text-foreground font-mono shadow-none focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_15px_-3px_var(--color-primary)] transition-all outline-none placeholder:text-transparent focus-visible:placeholder:text-muted-foreground/50" />}
                    </FormControl>
                    <FormLabel className={cn("absolute left-0 transition-all pointer-events-none font-sans font-bold uppercase tracking-wider", field.value ? "top-0 text-[10px] text-primary" : "top-8 text-sm text-muted-foreground peer-focus:top-0 peer-focus:text-[10px] peer-focus:text-primary")}>
                        {label} {required && <span className="text-primary ml-1">*</span>}
                    </FormLabel>
                    {helpText && <FormDescription className="text-[10px] mt-2 text-muted-foreground/60 font-mono italic">
                            {helpText}
                        </FormDescription>}
                    <FormMessage className="text-[10px] mt-1 absolute bottom-0 font-mono text-primary" />
                </FormItem>} />;
};
function StartProjectFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("Details Submitted", {
        description: "We are reviewing your property data. Expect a cash offer within 24 hours.",
        duration: 5000
      });
      resetForm();
    }
    if (submitError) {
      toast.error("Submission Error", {
        description: submitError || "Please verify your information and try again."
      });
    }
  }, [isSubmitted, submitError, resetForm]);
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <FloatingField control={control} name="name" label="Full Name" type="text" required={true} placeholder="John Smith" helpText="Legal owner or authorized representative" rules={{
        required: "Name is required"
      }} />
                <FloatingField control={control} name="email" label="Contact Email" type="email" required={true} placeholder="john@example.com" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <FloatingField control={control} name="phone" label="Phone Number" type="tel" required={false} placeholder="(555) 000-0000" helpText="For quick communication regarding your offer" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Invalid phone number"
        }
      }} />
                <FloatingField control={control} name="budget" label="Property State" type="select" required={false} options={["Arizona", "California", "Colorado", "Florida", "Nevada", "Texas", "Other"]} />
            </div>

            <FloatingField control={control} name="timeline" label="Estimated Acreage" type="select" required={false} options={["0–1 Acre", "1–5 Acres", "5–20 Acres", "20–50 Acres", "50+ Acres"]} />

            <FloatingField control={control} name="details" label="APN or Property Description" type="textarea" required={true} placeholder="Enter Assessor's Parcel Number (APN) or a brief description of the location..." helpText="Accurate property identifiers allow us to provide a faster cash offer." rules={{
      required: "Property details are required",
      minLength: {
        value: 5,
        message: "Please provide more detail"
      }
    }} />

            <div className="pt-6 flex justify-center">
                <Button type="submit" size="lg" variant="secondary" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] h-14 text-sm font-sans font-extrabold uppercase tracking-widest rounded-none border border-primary bg-primary text-primary-foreground hover:bg-transparent hover:text-primary active:scale-[0.98] transition-all duration-300 shadow-lg shadow-primary/10">
                    {isSubmitting ? <>
                            <Loader className="animate-spin mr-2 size-5" />
                            Processing...
                        </> : <>
                            Get My Cash Offer
                            <Send className="ml-2 size-4" />
                        </>}
                </Button>
            </div>
        </Form>;
}
export default function StartYourProject() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="123026" id="property-details-inquiry" className="py-24 dark bg-background relative overflow-hidden">
            <Toaster position="bottom-right" />
            
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div ref={ref} className={cn("text-center mb-16 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
                    <span className="text-primary font-mono tracking-widest uppercase text-xs mb-3 block">
                        Property Valuation
                    </span>
                    <h2 className="text-5xl md:text-6xl font-extrabold font-sans text-foreground mb-6 tracking-tighter">
                        Request Your Offer
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-sans leading-relaxed">
                        We buy vacant land nationwide for cash. Provide your property details below to initiate our rapid valuation process. No fees, no commissions.
                    </p>
                </div>

                <div className={cn("bg-card/40 backdrop-blur-md rounded-sm shadow-2xl p-8 md:p-12 border border-primary/20 transition-all duration-700 delay-200 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
                    <FormProvider formKey="property_details_inquiry_sell_your_land" formId="property_details_inquiry_sell_your_land" sectionName="Property Details Inquiry" defaultValues={{
          name: "",
          email: "",
          phone: "",
          budget: "",
          timeline: "",
          details: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          details: {
            required: true
          }
        }} submitText="Get My Cash Offer" successMessage="Inquiry received. Our team is reviewing your property data.">
                        <StartProjectFormInner />
                    </FormProvider>
                </div>
            </div>
            
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute -top-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-primary/5 blur-[140px]" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-secondary/5 blur-[140px]" />
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/graphy-dark.png')] opacity-[0.03]" />
            </div>
        </section>;
}